import React from 'react';

import { Tooltip } from '@design-system/components';
import { DSCheckMarkIcon, DSInformationIcon } from '@ds-icons';
import {
  PAYMENT_PLANS,
  ORGANIZATION_PLAN_FREE,
  ORGANIZATION_PLAN_ENTERPRISE,
  ORGANIZATION_PLAN_PRO_SPANS
} from '@shared/constants';
import isBoolean from 'lodash/isBoolean';

const renderCell = (rowData, value, key) => {
  const { tooltipMessage } = rowData;

  if (isBoolean(value)) {
    return value ? (
      <DSCheckMarkIcon className="checkMarkIcon" />
    ) : (
      <div className="no-payment-value" />
    );
  }

  if (tooltipMessage && key === 'feature') {
    return (
      <div className="table-info-cell">
        {value}
        <Tooltip content={tooltipMessage}>
          <DSInformationIcon className="table-info-icon" />
        </Tooltip>
      </div>
    );
  }

  return value;
};

export const FEATURE_COLUMN_KEY = 'feature';

export const EXPERIMENT_MANAGEMENT_COMPARE_PLANS_SECONDARY_COLUMNS = [
  {
    key: FEATURE_COLUMN_KEY,
    label: '',
    render: renderCell,
    className: 'feature-column'
  },
  {
    key: ORGANIZATION_PLAN_FREE,
    label: PAYMENT_PLANS[ORGANIZATION_PLAN_FREE].displayName,
    render: renderCell,
    headStyle: { fontSize: '18px' },
    align: 'center',
    headAlign: 'center'
  },
  {
    key: ORGANIZATION_PLAN_PRO_SPANS,
    label: PAYMENT_PLANS[ORGANIZATION_PLAN_PRO_SPANS].displayName,
    render: renderCell,
    headStyle: { fontSize: '18px' },
    align: 'center',
    headAlign: 'center'
  },
  {
    key: ORGANIZATION_PLAN_ENTERPRISE,
    label: PAYMENT_PLANS[ORGANIZATION_PLAN_ENTERPRISE].displayName,
    render: renderCell,
    headStyle: { fontSize: '18px' },
    align: 'center',
    headAlign: 'center'
  }
];

export const EXPERIMENT_MANAGEMENT_COMPARE_PLANS_COLUMNS = [
  {
    key: FEATURE_COLUMN_KEY,
    label: 'Experiment Management',
    className: 'feature-column'
  },
  {
    key: ORGANIZATION_PLAN_FREE,
    label: '',
    render: renderCell,
    align: 'center',
    headAlign: 'center'
  },
  {
    key: ORGANIZATION_PLAN_PRO_SPANS,
    label: '',
    align: 'center',
    headAlign: 'center',
    render: renderCell
  },
  {
    key: ORGANIZATION_PLAN_ENTERPRISE,
    label: '',
    render: renderCell,
    align: 'center',
    headAlign: 'center'
  }
];

export const EXPERIMENT_MANAGEMENT_COMPARE_PLANS_ROWS = [
  {
    key: 'experimentTracking',
    feature: 'Experiment tracking',
    render: renderCell,
    tooltipMessage:
      'Systematically record, compare, and analyze your machine learning training runs to accelerate development and improve model performance.',
    [ORGANIZATION_PLAN_FREE]: true,
    [ORGANIZATION_PLAN_PRO_SPANS]: true,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  },
  {
    key: 'pythonVisualizations',
    feature: 'Python visualisations',
    render: renderCell,
    tooltipMessage:
      'Create tailored visualizations using Python to customize your Comet dashboards, enhancing data insights for your specific needs.',
    [ORGANIZATION_PLAN_FREE]: true,
    [ORGANIZATION_PLAN_PRO_SPANS]: true,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  },
  {
    key: 'modelRegistry',
    feature: 'Model registry',
    render: renderCell,
    tooltipMessage:
      "Model Registry centralizes and organizes machine learning models, enabling versioning, tracking, and seamless deployment across your team's workflow.",
    [ORGANIZATION_PLAN_FREE]: true,
    [ORGANIZATION_PLAN_PRO_SPANS]: true,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  },
  {
    key: 'datasetManagementAndVersioning',
    feature: 'Dataset management and versioning',
    render: renderCell,
    tooltipMessage:
      'Track and version training datasets, linking them to experiments for complete model lineage and reproducibility.',
    [ORGANIZATION_PLAN_FREE]: true,
    [ORGANIZATION_PLAN_PRO_SPANS]: true,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  },
  {
    key: 'hyperparameterSearch',
    feature: 'Hyperparameter search',
    render: renderCell,
    tooltipMessage:
      'Optimize model performance with automated hyperparameter tuning, tracking all results within the Comet platform.',
    [ORGANIZATION_PLAN_FREE]: true,
    [ORGANIZATION_PLAN_PRO_SPANS]: true,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  },
  {
    key: 'monthlyLimits',
    feature: 'Monthly limits',
    [ORGANIZATION_PLAN_FREE]: 'Fair usage policy',
    [ORGANIZATION_PLAN_PRO_SPANS]: (
      <>
        <p>1500 training hours included</p>
        <p>$1/training hours</p>
      </>
    ),
    [ORGANIZATION_PLAN_ENTERPRISE]: 'Unlimited'
  },
  {
    key: 'dataUsage',
    feature: 'Data usage',
    [ORGANIZATION_PLAN_FREE]: '100GB',
    [ORGANIZATION_PLAN_PRO_SPANS]: (
      <>
        <p>500GB included</p>
        <p>$3/100GB/month</p>
      </>
    ),
    [ORGANIZATION_PLAN_ENTERPRISE]: 'Unlimited'
  },
  {
    key: 'teamMembers',
    feature: 'Team members',
    [ORGANIZATION_PLAN_FREE]: '1',
    [ORGANIZATION_PLAN_PRO_SPANS]: 'Up to 10 users',
    [ORGANIZATION_PLAN_ENTERPRISE]: 'Unlimited'
  }
];

export const LLM_EVALUATION_COMPARE_PLANS_COLUMNS = [
  {
    key: FEATURE_COLUMN_KEY,
    label: 'LLM Evaluation',
    render: renderCell,
    className: 'feature-column'
  },
  {
    key: ORGANIZATION_PLAN_FREE,
    label: '',
    render: renderCell,
    align: 'center',
    headAlign: 'center'
  },
  {
    key: ORGANIZATION_PLAN_PRO_SPANS,
    label: '',
    render: renderCell,
    align: 'center',
    headAlign: 'center'
  },
  {
    key: ORGANIZATION_PLAN_ENTERPRISE,
    label: '',
    render: renderCell,
    align: 'center',
    headAlign: 'center'
  }
];
export const LLM_EVALUATION_COMPARE_PLANS_ROWS = [
  {
    key: 'tracing',
    feature: 'Tracing',
    render: renderCell,
    tooltipMessage:
      'Monitor and analyze the performance of your language models during development and in production, capturing inputs, outputs, and metadata for each inference.',
    [ORGANIZATION_PLAN_FREE]: true,
    [ORGANIZATION_PLAN_PRO_SPANS]: true,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  },
  {
    key: 'datasets',
    feature: 'Datasets',
    render: renderCell,
    tooltipMessage:
      'Manage and version collections of prompts and examples used for evaluating LLM application performance, ensuring consistent and reproducible assessments.',
    [ORGANIZATION_PLAN_FREE]: true,
    [ORGANIZATION_PLAN_PRO_SPANS]: true,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  },
  {
    key: 'judgeMetrics',
    feature: 'LLM as a judge metrics',
    [ORGANIZATION_PLAN_FREE]: true,
    [ORGANIZATION_PLAN_PRO_SPANS]: true,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  },
  {
    key: 'productionMonitoring',
    feature: 'Production monitoring',
    render: renderCell,
    tooltipMessage:
      'Track your LLM applications in production with our production ready LLM Evaluation platform.',
    [ORGANIZATION_PLAN_FREE]: true,
    [ORGANIZATION_PLAN_PRO_SPANS]: true,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  },
  {
    key: 'teamMembers',
    feature: 'Team members',
    [ORGANIZATION_PLAN_FREE]: 'Unlimited',
    [ORGANIZATION_PLAN_PRO_SPANS]: 'Unlimited',
    [ORGANIZATION_PLAN_ENTERPRISE]: 'Unlimited'
  }
];

export const LLM_MONTHLY_USAGE_PLANS_COLUMNS = [
  {
    key: FEATURE_COLUMN_KEY,
    label: 'Monthly Usage',
    render: renderCell,
    className: 'feature-column'
  },
  {
    key: ORGANIZATION_PLAN_FREE,
    label: '',
    render: renderCell,
    align: 'center',
    headAlign: 'center'
  },
  {
    key: ORGANIZATION_PLAN_PRO_SPANS,
    label: '',
    render: renderCell,
    align: 'center',
    headAlign: 'center'
  },
  {
    key: ORGANIZATION_PLAN_ENTERPRISE,
    label: '',
    render: renderCell,
    align: 'center',
    headAlign: 'center'
  }
];
export const LLM_MONTHLY_USAGE_PLANS_ROWS = [
  {
    key: 'spansIncluded',
    feature: 'Spans included',
    render: renderCell,
    tooltipMessage:
      'A span represents a single tracked operation within your LLM pipeline, such as a model request, a function call or an agent action.',
    [ORGANIZATION_PLAN_FREE]: '25k',
    [ORGANIZATION_PLAN_PRO_SPANS]: '100k',
    [ORGANIZATION_PLAN_ENTERPRISE]: 'Unlimited'
  },
  {
    key: 'additionalSpans',
    feature: 'Additional spans',
    render: renderCell,
    [ORGANIZATION_PLAN_FREE]: false,
    [ORGANIZATION_PLAN_PRO_SPANS]: '$5/100k spans',
    [ORGANIZATION_PLAN_ENTERPRISE]: 'Unlimited'
  },
  {
    key: 'spanDataRetention',
    feature: 'Span data retention',
    [ORGANIZATION_PLAN_FREE]: '60 days',
    [ORGANIZATION_PLAN_PRO_SPANS]: '60 days',
    [ORGANIZATION_PLAN_ENTERPRISE]: 'Custom'
  },
  {
    key: 'additionalSpanRetention',
    feature: 'Additional span retention',
    render: renderCell,
    tooltipMessage: 'Increase span data retention from 60 days to 400 days.',
    [ORGANIZATION_PLAN_FREE]: false,
    [ORGANIZATION_PLAN_PRO_SPANS]: '$29/100k spans',
    [ORGANIZATION_PLAN_ENTERPRISE]: 'Custom'
  }
];

export const MPM_COMPARE_PLANS_COLUMNS = [
  {
    key: FEATURE_COLUMN_KEY,
    label: 'Model Production Monitoring',
    render: renderCell,
    className: 'feature-column'
  },
  {
    key: ORGANIZATION_PLAN_FREE,
    label: '',
    render: renderCell,
    align: 'center',
    headAlign: 'center'
  },
  {
    key: ORGANIZATION_PLAN_PRO_SPANS,
    label: '',
    render: renderCell,
    align: 'center',
    headAlign: 'center'
  },
  {
    key: ORGANIZATION_PLAN_ENTERPRISE,
    label: '',
    render: renderCell,
    align: 'center',
    headAlign: 'center'
  }
];
export const MPM_COMPARE_PLANS_ROWS = [
  {
    key: 'dataDriftDetection',
    feature: 'Data drift detection',
    render: renderCell,
    tooltipMessage:
      'Automatically identify data drift for your Machine Learning models in production, helping maintain model performance over time.',
    [ORGANIZATION_PLAN_FREE]: false,
    [ORGANIZATION_PLAN_PRO_SPANS]: false,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  },
  {
    key: 'featureDistributionAnalysis',
    feature: 'Feature distribution analysis',
    render: renderCell,
    tooltipMessage:
      'Visualize and monitor input feature distributions to detect shifts and anomalies in your data.',
    [ORGANIZATION_PLAN_FREE]: false,
    [ORGANIZATION_PLAN_PRO_SPANS]: false,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  },
  {
    key: 'customMetrics',
    feature: 'Custom metrics',
    render: renderCell,
    tooltipMessage:
      'Create and track tailored performance metrics using SQL queries on your logged data.',
    [ORGANIZATION_PLAN_FREE]: false,
    [ORGANIZATION_PLAN_PRO_SPANS]: false,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  },
  {
    key: 'alerts',
    feature: 'Alerts',
    render: renderCell,
    tooltipMessage:
      'Set up custom notifications for critical events via Slack, email, and other channels.',
    [ORGANIZATION_PLAN_FREE]: false,
    [ORGANIZATION_PLAN_PRO_SPANS]: false,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  }
];

export const PLATFORM_COMPARE_PLANS_COLUMNS = [
  {
    key: FEATURE_COLUMN_KEY,
    label: 'Platform',
    render: renderCell,
    className: 'feature-column'
  },
  {
    key: ORGANIZATION_PLAN_FREE,
    label: '',
    render: renderCell,
    align: 'center',
    headAlign: 'center'
  },
  {
    key: ORGANIZATION_PLAN_PRO_SPANS,
    label: '',
    render: renderCell,
    align: 'center',
    headAlign: 'center'
  },
  {
    key: ORGANIZATION_PLAN_ENTERPRISE,
    label: '',
    render: renderCell,
    align: 'center',
    headAlign: 'center'
  }
];
export const PLATFORM_COMPARE_PLANS_ROWS = [
  {
    key: 'flexible',
    feature: 'Flexible deployments',
    render: renderCell,
    tooltipMessage:
      'Choose from multiple deployment options, including cloud-based, on-premises, and fully managed solutions to fit your infrastructure needs.',
    [ORGANIZATION_PLAN_FREE]: false,
    [ORGANIZATION_PLAN_PRO_SPANS]: false,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  },
  {
    key: 'single-sign-on',
    feature: 'Single Sign-On',
    render: renderCell,
    tooltipMessage:
      'Streamline authentication with enterprise-grade SSO, supporting OAuth 2.0, SAML, and LDAP protocols.',
    [ORGANIZATION_PLAN_FREE]: false,
    [ORGANIZATION_PLAN_PRO_SPANS]: false,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  },
  {
    key: 'serviceAccounts',
    feature: 'Service accounts',
    render: renderCell,
    tooltipMessage:
      'Create dedicated machine-to-machine accounts for secure, automated data logging and API access.',
    [ORGANIZATION_PLAN_FREE]: false,
    [ORGANIZATION_PLAN_PRO_SPANS]: false,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  },
  {
    key: 'viewOnlyUsers',
    feature: 'View only users',
    render: renderCell,
    tooltipMessage:
      'Grant read-only access to team members for data exploration without the ability to modify or log new data.',
    [ORGANIZATION_PLAN_FREE]: false,
    [ORGANIZATION_PLAN_PRO_SPANS]: false,
    [ORGANIZATION_PLAN_ENTERPRISE]: true
  },
  {
    key: 'support',
    feature: 'Support',
    render: renderCell,
    tooltipMessage:
      'Access tiered support options including email, Slack, phone, and dedicated team assistance based on your plan.',
    [ORGANIZATION_PLAN_FREE]: 'Community Slack',
    [ORGANIZATION_PLAN_PRO_SPANS]: 'Email',
    [ORGANIZATION_PLAN_ENTERPRISE]: 'Dedicated team'
  }
];
